import { Link } from 'components/link';

/** Props for {@link PropertyIdLink}. */
interface PropertyIdLinkProps {
  /** The ID shown. */
  propertyId: string;
  /** The property's well, when it has one. */
  uwi: string | null;
  /** The property's unit page, when it has a unit. */
  unitHref: string | null;
}

/**
 * A property's ID, linked in a new tab to its well's page, else (a Slot Ref,
 * with no well) to its unit's; plain text when it has neither.
 *
 * @param {PropertyIdLinkProps} props - Component props
 * @return {JSX.Element | string} The ID, linked when it can be
 */
export function PropertyIdLink({
  propertyId,
  uwi,
  unitHref,
}: PropertyIdLinkProps) {
  const to = uwi ? `/wells/${encodeURIComponent(uwi)}` : unitHref;
  if (!to) return propertyId;
  return (
    <Link to={to} target="_blank" rel="noopener noreferrer">
      {propertyId}
    </Link>
  );
}

/** Props for {@link UnitLink}. */
interface UnitLinkProps {
  /** The property's unit, when it has one. */
  unitName: string | null;
  /** The unit's page, when it has one. */
  unitHref: string | null;
}

/**
 * A property's unit, linked in a new tab to the unit's page when it has one;
 * a dash when the property has no unit.
 *
 * @param {UnitLinkProps} props - Component props
 * @return {JSX.Element | string} The unit, linked when it can be
 */
export function UnitLink({ unitName, unitHref }: UnitLinkProps) {
  if (!unitName) return '—';
  if (!unitHref) return unitName;
  return (
    <Link to={unitHref} target="_blank" rel="noopener noreferrer">
      {unitName}
    </Link>
  );
}
